import type { JSX } from 'react';

import { Text, ThemeProvider } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';

import { AmountSelector } from '../components/AmountSelector';
import { ContinueButton } from '../components/ContinueButton';
import { NewPaymentMethodPaypal } from '../components/NewPaymentMethodPaypal';
import { ValidationError } from '../components/ValidationError';
import type { ModalCallbackProps } from '../shared/types';
import { NoSelection } from '../sunriseHoldoutExperiment/components/NoSelection';
import { YesSelection } from '../sunriseHoldoutExperiment/components/YesSelection';
import { useSunriseHoldoutExperiment } from '../sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

import { useMembershipForm } from './hooks/useMembershipForm';
import { useUserEventListeners } from './hooks/useUserEventListeners';

function Alert() {
	const { translate } = useI18n();

	return (
		<div data-testid="sunriseHoldoutExperiment-noSelectionMade-error">
			<ThemeProvider>
				<Flex
					sx={{
						flexDirection: 'column',
						alignItems: 'flex-start',
						alignSelf: 'stretch',
						borderRadius: '8px',
						border: '1px solid #FF9DAD',
						background: '#FEF1F3',
						marginBottom: '24px',
					}}
				>
					<Flex
						sx={{
							flex: '1 1 0',
							flexDirection: 'column',
							alignItems: 'flex-start',
							gap: '12px',
							padding: '24px',
							color: '#910A21',
						}}
					>
						<Text variant="body" size="large" fontWeight="bold">
							{translate('corgi.co.ppmu.modal.validation.no_selection_made.header')}
						</Text>
						<Text>{translate('corgi.co.ppmu.modal.validation.no_selection_made.body')}</Text>
					</Flex>
				</Flex>
			</ThemeProvider>
		</div>
	);
}

export function MembershipForm({ onAccept, onDecline }: ModalCallbackProps): JSX.Element {
	const {
		actions: { ...useMembershipFormActions },
		data: {
			qaSuffix,
			copy: { yes, no },
			...useMembershipFormData
		},
	} = useMembershipForm({ onAccept, onDecline });
	const {
		isSunriseEnabled,
		onSubmit,
		yesSelected,
		onSelectYes,
		noSelected,
		onSelectNo,
		acceptMembership,
		...sunriseHoldoutExperiment
	} = useSunriseHoldoutExperiment({
		acceptMembership: useMembershipFormData.acceptMembership,
		onSubmit: useMembershipFormActions.onSubmit,
		onSelectYes: useMembershipFormActions.onSelectYes,
		onSelectNo: useMembershipFormActions.onSelectNo,
		isSunriseEnabled: useMembershipFormData.isSunriseEnabled,
	});

	useUserEventListeners({ onAccept, onDecline });

	return (
		<form onSubmit={onSubmit} sx={{ mt: 24 }}>
			{sunriseHoldoutExperiment.noSelectionMadeError && <Alert />}
			<Flex sx={{ flexDirection: 'column', gap: '16px' }}>
				<YesSelection
					qaSelectorSelection="ppmu-modal-selection-true"
					qaSelectorLabel={`ppmu-modal-selection-yes-title${qaSuffix}`}
					qaSelectorMessage={`ppmu-modal-selection-yes-description${qaSuffix}`}
					label={yes.label}
					message={yes.message}
					highlight={yes.highlight}
					checked={yesSelected}
					onSelect={onSelectYes}
					isSunriseEnabled={isSunriseEnabled}
				>
					<AmountSelector acceptMembership={acceptMembership} />
				</YesSelection>
				<NoSelection
					qaSelectorSelection="ppmu-modal-selection-false"
					qaSelectorLabel="ppmu-modal-selection-no-title"
					qaSelectorMessage={`ppmu-modal-selection-no-description${qaSuffix}`}
					label={no.label}
					message={no.message}
					checked={noSelected}
					onSelect={onSelectNo}
					isSunriseEnabled={isSunriseEnabled}
				/>
				<ValidationError />
				<Box>
					<ContinueButton />
					<NewPaymentMethodPaypal onAccept={onAccept} onDecline={onDecline} />
				</Box>
			</Flex>
		</form>
	);
}
